import React from 'react';
import reduce from 'lodash/reduce';
import take from 'lodash/take';
import classNames from 'classnames';
import { formatUrl } from 'url-lib';

import { GRID_TYPE } from 'eventbrite_design_system/mediaCardContent/constants';
import EventCard from 'eventbrite_design_system/eventCard/EnhancedEventCard';

import { OnboardingEntry } from '@eb/destination-utils';

import {
    TRACKING_CODE,
    UTM_HOME_FEED_TERM,
    UTM_HOME_TERM,
} from '../constants/analytics';

import './ConsumerOnboardingCard.scss';

const _createShareUrl = (url, isAuthenticated) =>
    formatUrl(url, {
        'utm-medium': 'discovery',
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-source': 'cp',
        'utm-term': isAuthenticated ? UTM_HOME_FEED_TERM : UTM_HOME_TERM,
    });

const _insertOnboardingCard = ({
    cardsComponent,
    eventCardContainerClassName,
    isAuthenticated,
    isInOddLayout,
    onClickOnBoardingCard,
}) => {
    const [card1, card2, card3, ...remainingCards] = cardsComponent;

    let cardsWithOnboarding = [
        card3,
        <OnboardingEntry
            type="standardPurple"
            containerClasses={eventCardContainerClassName}
            key="onboarding-entry"
            isAuthenticated={isAuthenticated}
            onButtonClick={onClickOnBoardingCard.bind(null)}
        />,
    ];

    if (isInOddLayout) {
        cardsWithOnboarding = [
            <OnboardingEntry
                type="standardPurple"
                containerClasses={eventCardContainerClassName}
                key="onboarding-entry"
                isAuthenticated={isAuthenticated}
                onButtonClick={onClickOnBoardingCard.bind(null)}
            />,
            card3,
        ];
    }

    const _cardsComponent = [
        card1,
        card2,
        ...cardsWithOnboarding,
        ...take(remainingCards, remainingCards.length),
    ];

    return _cardsComponent;
};

const ConsumerCards = ({
    bucketKey,
    bucketLayoutConfig,
    eventsGroups,
    isAuthenticated,
    onViewEvent,
    onSaveEvent,
    visible,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
    userCollections,
    isFreeTab,
    shouldShowOnboardingEntryPoint,
    cardType,
    isInOddLayout,
    shareOptions,
    onWillShareClick,
    onClickOnBoardingCard,
    shouldShowOnboardingCard,
    featureFlags,
}) => {
    const bucketClassNames =
        (bucketLayoutConfig && bucketLayoutConfig.bucketClassNames) || '';

    let cardsComponent = reduce(
        eventsGroups,
        (memo, { events, containerClasses }) => {
            const eventCardsComponents = events.map(
                ({
                    id,
                    isFree,
                    isSoldOut,
                    imageUrl,
                    startDate,
                    hideStartDate,
                    ...cardProps
                }) => (
                    <div className={containerClasses} key={id}>
                        <EventCard
                            id={id}
                            onClick={onViewEvent.bind(null, id, bucketKey)}
                            onSaveEvent={onSaveEvent}
                            isAuthenticated={isAuthenticated}
                            excludeDateThumbnail={true}
                            hidePriceDescription={true}
                            onAddOrRemoveEventToCollection={
                                onAddOrRemoveEventToCollection
                            }
                            onCreateCollection={onCreateCollection}
                            userCollections={userCollections}
                            isConsumer={true}
                            isLazyImage={!visible}
                            startDate={hideStartDate ? null : startDate}
                            {...cardProps}
                            isFree={!isFreeTab && isFree}
                            isSoldOut={isSoldOut}
                            imageUrl={imageUrl}
                            onWillShareClick={onWillShareClick.bind(null, id)}
                            shareOptions={{
                                ...shareOptions,
                                utmOptions: {
                                    'utm-term': isAuthenticated
                                        ? UTM_HOME_FEED_TERM
                                        : UTM_HOME_TERM,
                                },
                                eventUrl: _createShareUrl(
                                    cardProps.url,
                                    isAuthenticated,
                                ),
                                trackingCode: TRACKING_CODE,
                            }}
                            shouldConvertToUserTimezone={
                                featureFlags &&
                                featureFlags.launchLocatedDatesForOnlineEvents
                            }
                        />
                    </div>
                ),
            );

            return [...memo, ...eventCardsComponents];
        },
        [],
    );

    /*
        shouldShowOnboardingCard: Property of bucket defined in tabConfig
        shouldShowOnboardingEntryPoint: Feature Flag of Onboarding entry point
    */
    if (shouldShowOnboardingCard && shouldShowOnboardingEntryPoint) {
        const eventCardContainerClassName = classNames(
            'feed__card-cell',
            'eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1',
            {
                'eds-g-cell-sm-6-12 eds-g-cell-mn-4-12 eds-g-cell-lg-3-12':
                    cardType === GRID_TYPE,
            },
        );

        cardsComponent = _insertOnboardingCard({
            cardsComponent,
            eventCardContainerClassName,
            isAuthenticated,
            isInOddLayout,
            onClickOnBoardingCard,
        });
    }

    return <div className={bucketClassNames}>{cardsComponent}</div>;
};

export default ConsumerCards;
